import React, { useState, useRef, useEffect } from 'react';
import SignatureModal from '../SignatureModal';

interface NoticeOfPrivacyPracticesProps {
  onAccept: (signature: string) => void;
}

const NoticeOfPrivacyPractices: React.FC<NoticeOfPrivacyPracticesProps> = ({ onAccept }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signature, setSignature] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
      setHasScrolledToBottom(isAtBottom);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAgreed(e.target.checked);
  };

  const handleSignatureComplete = (signatureData: string, signatureType: 'drawn' | 'typed', typedName?: string) => {
    setSignature(signatureData);
    setShowSignatureModal(false);
  };

  const handleSubmit = () => {
    if (signature) {
      onAccept(signature);
    }
  };

  return (
    <div className="app">
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        background: '#28527A',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: '#f9fafb', fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
            The Real Therapist of Atlanta, LLC.
          </h1>
          <p style={{ color: '#6CA6C1', margin: '0 0 1rem 0', fontSize: '1rem' }}>
            626 Main Street | Palmetto, GA. 30268 | 470.250.9113
          </p>
          <h1 style={{ color: '#f9fafb', fontSize: '1.75rem', margin: '0 0 0.5rem 0' }}>
            Notice of Privacy Practices
          </h1>
          <p style={{ color: '#6CA6C1', margin: 0 }}>
            Please read the privacy notice below and scroll to the bottom to continue
          </p>
        </div>

        {/* Privacy Notice Content */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          border: '1px solid #6CA6C1',
          marginBottom: '2rem'
        }}>
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            style={{
              height: '400px',
              overflowY: 'auto',
              padding: '2rem',
              color: '#d1d5db',
              fontSize: '0.9rem',
              lineHeight: '1.6'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#f9fafb', margin: '0 0 0.5rem 0' }}>
                EFFECTIVE DATE OF THIS NOTICE
              </h3>
              <p style={{ color: '#6CA6C1', margin: '0 0 1.5rem 0' }}>
                This notice went into effect on [INSERT DATE]
              </p>
            </div>

            <h2 style={{ color: '#f9fafb', marginBottom: '1.5rem', textAlign: 'center' }}>
              NOTICE OF PRIVACY PRACTICES
            </h2>

            <p style={{ marginBottom: '1.5rem', fontWeight: 'bold', textAlign: 'center', textTransform: 'uppercase' }}>
              THIS NOTICE DESCRIBES HOW HEALTH INFORMATION MAY BE USED AND DISCLOSED AND HOW YOU CAN GET ACCESS TO THIS INFORMATION. PLEASE REVIEW IT CAREFULLY.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              I. MY PLEDGE REGARDING HEALTH INFORMATION:
            </h3>
            <p style={{ marginBottom: '1rem' }}>
              I understand that health information about you and your health care is personal. I am committed to protecting health information about you. I create a record of the care and services you receive from me. I need this record to provide you with quality care and to comply with certain legal requirements. This notice applies to all of the records of your care generated by this mental health care practice. This notice will tell you about the ways in which I may use and disclose health information about you. I also describe your rights to the health information I keep about you, and describe certain obligations I have regarding the use and disclosure of your health information.
            </p>
            <p style={{ marginBottom: '1rem' }}>I am required by law to:</p>
            <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>Make sure that protected health information ("PHI") that identifies you is kept private.</li>
              <li style={{ marginBottom: '0.5rem' }}>Give you this notice of my legal duties and privacy practices with respect to health information.</li>
              <li style={{ marginBottom: '0.5rem' }}>Follow the terms of the notice that is currently in effect.</li>
              <li style={{ marginBottom: '0.5rem' }}>I can change the terms of this Notice, and such changes will apply to all information I have about you. The new Notice will be available upon request, in my office, and on my website.</li>
            </ul>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              II. HOW I MAY USE AND DISCLOSE HEALTH INFORMATION ABOUT YOU:
            </h3>
            <p style={{ marginBottom: '1rem' }}>
              The following categories describe different ways that I use and disclose health information. For each category of uses or disclosures I will explain what I mean and try to give some examples. Not every use or disclosure in a category will be listed. However, all of the ways I am permitted to use and disclose information will fall within one of the categories.
            </p>

            <h4 style={{ color: '#f9fafb', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              For Treatment Payment, or Health Care Operations:
            </h4>
            <p style={{ marginBottom: '1rem' }}>
              Federal privacy rules (regulations) allow health care providers who have direct treatment relationship with the patient/client to use or disclose the patient/client's personal health information without the patient's written authorization, to carry out the health care provider's own treatment, payment or health care operations. I may also disclose your protected health information for the treatment activities of any health care provider. This too can be done without your written authorization. For example, if a clinician were to consult with another licensed health care provider about your condition, we would be permitted to use and disclose your personal health information, which is otherwise confidential, in order to assist the clinician in diagnosis and treatment of your mental health condition.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              Disclosures for treatment purposes are not limited to the minimum necessary standard. Because therapists and other health care providers need access to the full record and/or full and complete information in order to provide quality care. The word "treatment" includes, among other things, the coordination and management of health care providers with a third party, consultations between health care providers and referrals of a patient for health care from one health care provider to another.
            </p>

            <h4 style={{ color: '#f9fafb', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              Lawsuits and Disputes:
            </h4>
            <p style={{ marginBottom: '1.5rem' }}>
              If you are involved in a lawsuit, I may disclose health information in response to a court or administrative order. I may also disclose health information about your child in response to a subpoena, discovery request, or other lawful process by someone else involved in the dispute, but only if efforts have been made to tell you about the request or to obtain an order protecting the information requested.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              III. CERTAIN USES AND DISCLOSURES REQUIRE YOUR AUTHORIZATION:
            </h3>

            <h4 style={{ color: '#f9fafb', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              1. Psychotherapy Notes.
            </h4>
            <p style={{ marginBottom: '0.75rem' }}>
              I do keep "psychotherapy notes" as that term is defined in 45 CFR § 164.501, and any use or disclosure of such notes requires your Authorization unless the use or disclosure is:
            </p>
            <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
              <li style={{ marginBottom: '0.25rem' }}>For my use in treating you.</li>
              <li style={{ marginBottom: '0.25rem' }}>For my use in training or supervising mental health practitioners to help them improve their skills in group, joint, family, or individual counseling or therapy.</li>
              <li style={{ marginBottom: '0.25rem' }}>For my use in defending myself in legal proceedings instituted by you.</li>
              <li style={{ marginBottom: '0.25rem' }}>For use by the Secretary of Health and Human Services to investigate my compliance with HIPAA.</li>
              <li style={{ marginBottom: '0.25rem' }}>Required by law and the use or disclosure is limited to the requirements of such law.</li>
              <li style={{ marginBottom: '0.25rem' }}>Required by law for certain health oversight activities pertaining to the originator of the psychotherapy notes.</li>
              <li style={{ marginBottom: '0.25rem' }}>Required by a coroner who is performing duties authorized by law.</li>
              <li style={{ marginBottom: '0.25rem' }}>Required to help avert a serious threat to the health and safety of others.</li>
            </ul>

            <h4 style={{ color: '#f9fafb', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              2. Marketing Purposes.
            </h4>
            <p style={{ marginBottom: '1rem' }}>
              As a psychotherapist, I will not use or disclose your PHI for marketing purposes.
            </p>

            <h4 style={{ color: '#f9fafb', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              3. Sale of PHI.
            </h4>
            <p style={{ marginBottom: '1.5rem' }}>
              As a psychotherapist, I will not sell your PHI in the regular course of my business.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              IV. CERTAIN USES AND DISCLOSURES DO NOT REQUIRE YOUR AUTHORIZATION.
            </h3>
            <p style={{ marginBottom: '1rem' }}>
              Subject to certain limitations in the law, I can use and disclose your PHI without your Authorization for the following reasons:
            </p>
            <ol style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>When disclosure is required by state or federal law, and the use or disclosure complies with and is limited to the relevant requirements of such law.</li>
              <li style={{ marginBottom: '0.5rem' }}>For public health activities, including reporting suspected child, elder, or dependent adult abuse, or preventing or reducing a serious threat to anyone's health or safety.</li>
              <li style={{ marginBottom: '0.5rem' }}>For health oversight activities, including audits and investigations.</li>
              <li style={{ marginBottom: '0.5rem' }}>For judicial and administrative proceedings, including responding to a court or administrative order, although my preference is to obtain an Authorization from you before doing so.</li>
              <li style={{ marginBottom: '0.5rem' }}>For law enforcement purposes, including reporting crimes occurring on my premises.</li>
              <li style={{ marginBottom: '0.5rem' }}>To coroners or medical examiners, when such individuals are performing duties authorized by law.</li>
              <li style={{ marginBottom: '0.5rem' }}>For research purposes, including studying and comparing the mental health of patients who received one form of therapy versus those who received another form of therapy for the same condition.</li>
              <li style={{ marginBottom: '0.5rem' }}>Specialized government functions, including ensuring the proper execution of military missions, protecting the President of the United States; conducting intelligence or counter-intelligence operations; or, helping to ensure the safety of those working within or housed in correctional institutions.</li>
              <li style={{ marginBottom: '0.5rem' }}>For workers' compensation purposes. Although my preference is to obtain an Authorization from you, I may provide your PHI in order to comply with workers' compensation laws.</li>
              <li style={{ marginBottom: '0.5rem' }}>Appointment reminders and health related benefits or services. I may use and disclose your PHI to contact you to remind you that you have an appointment with me. I may also use and disclose your PHI to tell you about treatment alternatives, or other health care services or benefits that I offer.</li>
            </ol>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              V. CERTAIN USES AND DISCLOSURES REQUIRE YOU TO HAVE THE OPPORTUNITY TO OBJECT.
            </h3>
            <h4 style={{ color: '#f9fafb', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              1. Disclosures to family, friends, or others.
            </h4>
            <p style={{ marginBottom: '1.5rem' }}>
              I may provide your PHI to a family member, friend, or other person that you indicate is involved in your care or the payment for your health care, unless you object in whole or in part. The opportunity to consent may be obtained retroactively in emergency situations.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              VI. YOU HAVE THE FOLLOWING RIGHTS WITH RESPECT TO YOUR PHI:
            </h3>
            <ol style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
              <li style={{ marginBottom: '0.75rem' }}>
                <strong>The Right to Request Limits Uses and Disclosures of Your PHI.</strong> You have the right to ask me not to use or disclose certain PHI for treatment, payment, or health care operations purposes. I am not required to agree to your request, and I may say "no" if I believe it would affect your health care.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                <strong>The Right to Request Restrictions for Out-of-Pocket Expenses Paid for In Full.</strong> You have the right to request restrictions on disclosures of your PHI to health plans for payment or health care operations purposes if the PHI pertains solely to a health care item or a health care service that you have paid for out-of-pocket in full.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                <strong>The Right to Choose How I Send PHI to You.</strong> You have the right to ask me to contact you in a specific way (for example, home or office phone) or to send mail to a different address, and I will agree to all reasonable requests.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                <strong>The Right to See and Get Copies of Your PHI.</strong> Other than "psychotherapy notes," you have the right to get an electronic or paper copy of your medical record and other information that I have about you. I will provide you with a copy of your record, or a summary of it, if you agree to receive a summary, within 30 days of receiving your written request, and I may charge a reasonable, cost based fee for doing so.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                <strong>The Right to Get a List of the Disclosures.</strong> You have the right to request a list of instances in which I have disclosed your PHI for purposes other than treatment, payment, or health care operations, or for which you provided me with an Authorization. I will respond to your request for an accounting of disclosures within 60 days of receiving your request. The list I will give you will include disclosures made in the last six years unless you request a shorter time. I will provide the list to you at no charge, but if you make more than one request in the same year, I will charge you a reasonable cost based fee for each additional request.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                <strong>The Right to Correct or Update Your PHI.</strong> If you believe that there is a mistake in your PHI, or that a piece of important information is missing from your PHI, you have the right to request that I correct the existing information or add the missing information. I may say "no" to your request, but I will tell you why in writing within 60 days of receiving your request.
              </li>
              <li style={{ marginBottom: '0.75rem' }}>
                <strong>The Right to Get a Paper or Electronic Copy of this Notice.</strong> You have the right get a paper copy of this Notice, and you have the right to get a copy of this notice by e-mail. And, even if you have agreed to receive this Notice via e-mail, you also have the right to request a paper copy of it.
              </li>
            </ol>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              Acknowledgement of Receipt of Privacy Notice
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Under the Health Insurance Portability and Accountability Act of 1996 (HIPAA), you have certain rights regarding the use and disclosure of your protected health information. By checking the box below, you are acknowledging that you have received a copy of HIPAA Notice of Privacy Practices.
            </p>

            <p style={{ marginBottom: '1.5rem', fontWeight: 'bold', color: '#f9fafb', textTransform: 'uppercase' }}>
              BY SIGNING BELOW I AM AGREEING THAT I HAVE READ, UNDERSTOOD AND AGREE TO THE ITEMS CONTAINED IN THIS DOCUMENT.
            </p>

            <p style={{ marginBottom: '0', fontSize: '0.8rem', color: '#d1d5db' }}>
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Agreement Section */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          padding: '1.5rem',
          border: '1px solid #6CA6C1',
          marginBottom: '1.5rem'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              color: hasScrolledToBottom ? '#f9fafb' : '#6b7280',
              cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed'
            }}>
              <input
                type="checkbox"
                checked={isAgreed}
                onChange={handleCheckboxChange}
                disabled={!hasScrolledToBottom}
                style={{
                  marginRight: '0.75rem',
                  width: '18px',
                  height: '18px',
                  cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed'
                }}
              />
              I have read, understood, and agree to the Notice of Privacy Practices
            </label>
          </div>

          {/* Signature Section */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: isAgreed ? '#f9fafb' : '#6b7280',
              marginBottom: '0.5rem',
              fontWeight: '500'
            }}>
              Digital Signature *
            </label>
            <button
              type="button"
              onClick={() => setShowSignatureModal(true)}
              disabled={!isAgreed}
              style={{
                width: '100%',
                padding: '3rem 1rem',
                borderRadius: '8px',
                border: '2px dashed #6CA6C1',
                background: isAgreed ? '#374151' : '#1f2937',
                color: isAgreed ? '#f9fafb' : '#6b7280',
                fontSize: '1rem',
                cursor: isAgreed ? 'pointer' : 'not-allowed',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
            >
              {signature ? (
                <div>
                  <div style={{ marginBottom: '0.5rem', color: '#10b981' }}>✓ Signature Captured</div>
                  <div style={{ fontSize: '0.875rem' }}>Click to update signature</div>
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: '0.5rem' }}>📝</div>
                  <div>Click to add your digital signature</div>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleSubmit}
            disabled={!signature}
            style={{
              background: signature
                ? 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)'
                : '#6b7280',
              color: 'white',
              padding: '0.75rem 3rem',
              fontSize: '1rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              cursor: signature ? 'pointer' : 'not-allowed',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              boxShadow: signature
                ? '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
                : 'none'
            }}
          >
            Submit Acknowledgment
          </button>
        </div>

        {/* Progress Indicator */}
        <div style={{
          textAlign: 'center',
          marginTop: '1.5rem',
          padding: '1rem',
          background: '#065f46',
          borderRadius: '8px',
          border: '1px solid #10b981'
        }}>
          <div style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
            Progress:
            <span style={{ color: hasScrolledToBottom ? '#10b981' : '#6b7280', marginLeft: '0.5rem' }}>
              ✓ Read Notice
            </span>
            <span style={{ color: isAgreed ? '#10b981' : '#6b7280', marginLeft: '1rem' }}>
              ✓ Accept Terms
            </span>
            <span style={{ color: signature ? '#10b981' : '#6b7280', marginLeft: '1rem' }}>
              ✓ Digital Signature
            </span>
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {showSignatureModal && (
        <SignatureModal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onSignatureComplete={handleSignatureComplete}
          title="Notice of Privacy Practices Signature"
          clientName="Client"
          previousSignature={signature ? {
            signatureData: signature,
            signatureType: 'drawn' as const
          } : undefined}
        />
      )}
    </div>
  );
};

export default NoticeOfPrivacyPractices;